import {
  DENY_REQUEST_ACCESS_BY_MAC,
  DENY_VIEW_CLOUD_DEVICES,
  hasPermission,
} from '@common/api/permission';
import { type Feature } from '@common/store/reducers/featureFlags';
import { UserLevel } from '@common/types/api';
import type User from '@common/types/models/User';

export type AppType = typeof APP_TYPE;
export type FeatureFlags = Partial<Record<Feature, boolean>>;

const customerRoles = [UserLevel.User, UserLevel.AdvancedUser];
const partnerRoles = [UserLevel.EnergyProvider, UserLevel.RetailerAdmin];
const stiebelRoles = [
  UserLevel.Technician,
  UserLevel.Developer,
  UserLevel.CountryManager,
  UserLevel.SuperAdmin,
];
const staffRoles = [...partnerRoles, ...stiebelRoles];
const managerRoles = [UserLevel.CountryManager, UserLevel.SuperAdmin];
const developerToolRoles = [UserLevel.SuperAdmin, UserLevel.Developer];

/** Every right of the current user; no user means no rights. */
export const getAccessRights = (
  user: User | undefined,
  featureFlags: FeatureFlags,
  appType: AppType,
) => {
  const role = user?.userLevel;
  const is = (roles: UserLevel[]) => !!role && roles.includes(role);

  return {
    // Show the end-user version of screens; app-dependent: plain Users count only in mystiebel
    isEndUser: is(appType === 'mystiebel' ? customerRoles : [UserLevel.AdvancedUser]),
    isSuperAdmin: is([UserLevel.SuperAdmin]),
    isPartner: is(partnerRoles),
    isEnergyProvider: is([UserLevel.EnergyProvider]),
    isCountryManager: is([UserLevel.CountryManager]),
    canUsePortalKeysTab: is(managerRoles),
    canUseFirmwaresTab: is([UserLevel.SuperAdmin]),
    canUseMassUpdatesTab: is([UserLevel.SuperAdmin]),
    canSeePartners: is(stiebelRoles),
    canEditOwner: is(managerRoles),
    canEditAdditionalUsers: is(managerRoles),
    canEditPartners: is(managerRoles),
    canEditContractType: is(stiebelRoles),
    canExportRetailers: is([UserLevel.SuperAdmin]),
    canSeeStatisticsTab: is(staffRoles),
    canAccessUserManagement: is(managerRoles),
    canFilterPartners: is(stiebelRoles),
    canUseDesignHelpers: is([UserLevel.SuperAdmin]) && !!featureFlags.DesignHelpers,
    canRestoreDevice: is(managerRoles),
    canExportDeviceList: is(staffRoles),
    canGetSendConfigFile: is(developerToolRoles),
    canAccessDebugSessions: is(developerToolRoles),
    canAccessDataManagement: is([UserLevel.AdvancedUser, ...managerRoles]),
    canSeeSpecialValuesInDiagrams: is(staffRoles),
    // Customer rather than staff (User or AdvancedUser) in every app; not app-dependent
    isCustomer: is(customerRoles),
    // Exactly the AdvancedUser role; differs from isEndUser in mystiebel, where plain Users are end users
    isAdvancedUser: is([UserLevel.AdvancedUser]),
    isStiebelEmployee: is(stiebelRoles),
    canDeleteDevice: is(managerRoles),
    canSeePortalKey: is([UserLevel.Technician, ...managerRoles]),
    canSeeEmptyOwner: is([UserLevel.Technician, UserLevel.SuperAdmin]),
    canAccessServiceWelt: is([UserLevel.AdvancedUser, ...staffRoles]),
    isStaff: is(staffRoles),
    canViewCloudDevices: !!role && !hasPermission(user, DENY_VIEW_CLOUD_DEVICES),
    canRequestAccessByMac: is([UserLevel.RetailerAdmin])
      && !hasPermission(user, DENY_REQUEST_ACCESS_BY_MAC),
  };
};

export type AccessRights = ReturnType<typeof getAccessRights>;
export type RightName = keyof AccessRights;
